'use client'

import BusinessIcon from '@mui/icons-material/Business'
import DownloadIcon from '@mui/icons-material/Download'
import GavelIcon from '@mui/icons-material/Gavel'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

const StyledPaper = styled(Paper)`
  padding: 24px;
  height: 100%;
  background: #1c1c1e;
  border: 1px solid #2a2a2a;
  border-radius: 12px;
`

// export const metadata = {
//   title: 'About Us - Polar Tensor LAB Limited',
//   description:
//     'Learn about Polar Tensor LAB Limited, our legal registrations, and corporate documents.',
// }

export default function AboutPage() {
  return (
    <Container maxWidth='lg' sx={{ py: 6 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant='h2'
          component='h1'
          gutterBottom
          sx={{ fontWeight: 700, mb: 2, color: '#f0f0f1' }}
        >
          About Polar Tensor
        </Typography>
        <Typography
          variant='h6'
          sx={{ maxWidth: 800, mx: 'auto', color: 'rgba(255, 255, 255, 0.7)' }}
        >
          Polar Tensor is internationally compliant with the fund registered in
          Panama, the Management in Hongkong and the MSB in the United States.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Company Overview */}
        <Grid size={12}>
          <StyledPaper>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ color: '#f0f0f1' }} />
              <Typography
                variant='h4'
                component='h2'
                sx={{ fontWeight: 600, color: '#f0f0f1' }}
              >
                Company Overview
              </Typography>
            </Box>
            <Typography variant='body1'>
              Polar Tensor LAB Limited is a quantitative finance and research company
              focused on innovative solutions in the financial sector.
              <br />
            </Typography>
            <Typography variant='body1'>
              We maintain active registrations and regulatory compliance in Panama,
              Hong Kong, and the United States, with all necessary documentation and
              filings accessible for transparency.
            </Typography>
          </StyledPaper>
        </Grid>

        {/* Hong Kong Registration */}
        <Grid size={{ xs: 12, md: 4 }}>
          <StyledPaper>
            <Typography
              variant='h5'
              component='h3'
              sx={{ mb: 3, fontWeight: 600, color: '#f0f0f1' }}
            >
              🇭🇰 Hong Kong Registration
            </Typography>
            <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
              Polar Tensor LAB Limited
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle2' color='text.secondary'>
                Registration Number
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                78737300
              </Typography>
            </Box>
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              fullWidth
              href='/honkong_incorporation.pdf'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                textDecoration: 'none',
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: '#f0f0f1',
                borderColor: '#2a2a2a',
                '&:hover': {
                  borderColor: '#2a2a2a',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Hong Kong Company Certificate
            </Button>
          </StyledPaper>
        </Grid>

        {/* Panama Registration */}
        <Grid size={{ xs: 12, md: 4 }}>
          <StyledPaper>
            <Typography
              variant='h5'
              component='h3'
              sx={{ mb: 3, fontWeight: 600, color: '#f0f0f1' }}
            >
              🇵🇦 Panama Registration
            </Typography>
            <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
              Polar Tensor Corp.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle2' color='text.secondary'>
                Registration Number
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                155771852
              </Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle2' color='text.secondary'>
                SEC (USA) CIK Number
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                0002085242
              </Typography>
            </Box>
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              fullWidth
              href='/panama_incorporation.pdf'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                textDecoration: 'none',
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: '#f0f0f1',
                borderColor: '#2a2a2a',
                '&:hover': {
                  borderColor: '#2a2a2a',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Panama Company Certificate
            </Button>
          </StyledPaper>
        </Grid>

        {/* USA/Montana Registration */}
        <Grid size={{ xs: 12, md: 4 }}>
          <StyledPaper>
            <Typography
              variant='h5'
              component='h3'
              sx={{ mb: 3, fontWeight: 600, color: '#f0f0f1' }}
            >
              🇺🇸 USA Registration
            </Typography>
            <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
              Polar MSB Inc.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' color='text.secondary'>
                Montana Registration Number
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                D1537006
              </Typography>
              <Link
                href='https://biz.sosmt.gov/search/business'
                target='_blank'
                rel='noopener noreferrer'
                sx={{ fontSize: '0.875rem' }}
              >
                View in Montana Business Registry →
              </Link>
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle2' color='text.secondary'>
                FinCEN Registration Number
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontFamily: 'monospace', fontWeight: 600 }}
              >
                31000306664168
              </Typography>
              <Link
                href='https://msb.fincen.gov/msb.registration.letter.php?ID=28315321'
                target='_blank'
                rel='noopener noreferrer'
                sx={{ fontSize: '0.875rem' }}
              >
                View FinCEN Registration →
              </Link>
            </Box>
            <Button
              variant='outlined'
              startIcon={<DownloadIcon />}
              fullWidth
              href='/legal_opinion.pdf'
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                textDecoration: 'none',
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: '#f0f0f1',
                borderColor: '#2a2a2a',
                '&:hover': {
                  borderColor: '#2a2a2a',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              USA Registration Documents
            </Button>
          </StyledPaper>
        </Grid>

        {/* Legal Documents */}
        <Grid size={12}>
          <StyledPaper>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <GavelIcon sx={{ color: '#f0f0f1' }} />
              <Typography
                variant='h4'
                component='h2'
                sx={{ fontWeight: 600, color: '#f0f0f1' }}
              >
                Legal Documents & Compliance
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
                  SEC 506(b) Filing
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                  Our SEC filing under Rule 506(b) of Regulation D, providing
                  exemption for private placements.
                </Typography>
                <Button
                  variant='outlined'
                  startIcon={<DownloadIcon />}
                  href='https://www.sec.gov/Archives/edgar/data/2085242/000208524225000001/xslFormDX01/primary_doc.xml'
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    mb: 2,
                    textDecoration: 'none',
                    color: '#f0f0f1',
                    borderColor: '#2a2a2a',
                    '&:hover': {
                      borderColor: '#2a2a2a',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  View SEC Filing
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
                  Legal Opinion
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                  Comprehensive legal opinion document outlining our regulatory
                  compliance and legal standing.
                </Typography>
                <Button
                  variant='outlined'
                  startIcon={<DownloadIcon />}
                  href='/legal_opinion.pdf'
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    textDecoration: 'none',
                    color: '#f0f0f1',
                    borderColor: '#2a2a2a',
                    '&:hover': {
                      borderColor: '#2a2a2a',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    },
                  }}
                >
                  Download Legal Opinion
                </Button>
              </Grid>
            </Grid>
          </StyledPaper>
        </Grid>

        {/* Compliance Statement */}
        <Grid size={12}>
          <Paper
            sx={{
              p: 3,
              background: '#1c1c1e',
              border: '1px solid #2a2a2a',
              borderRadius: '12px',
            }}
          >
            <Typography variant='h6' gutterBottom sx={{ color: '#f0f0f1' }}>
              Regulatory Compliance
            </Typography>
            <Typography variant='body2' sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              All registrations and filings are current and maintained in good
              standing. We are committed to full transparency and regulatory
              compliance across all jurisdictions in which we operate. For any
              questions regarding our legal status or to request additional
              documentation, please contact our legal team.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}
